/* Copyright (c) 2020-present CloudIO Inc */

export interface SyncEventMap {
  [key: string]: Record<string, unknown>;
}

export class SyncPubSub<T = SyncEventMap> {
  map = new Map<keyof T, ((event: keyof T, props: T[keyof T]) => void)[]>();

  set = new Set<string>();

  count = new Map<keyof T, number>();

  data = new Map<keyof T, unknown>();

  destroy = () => {
    this.map.clear();
    this.set.clear();
    this.count.clear();
    this.data.clear();
  };

  pub = <K extends keyof T>(
    event: K,
    props: T[K],
    deferrable = false,
    deferrableOnce = false,
  ) => {
    const listeners = this.map.get(event) ?? [];
    const len = listeners.length;
    // console.log('publishing event', event, { len, deferrable });
    let onceCalled = false;
    if (len > 0) {
      for (let i = 0; i < len; i++) {
        listeners[i](event, props);
      }
      onceCalled = true;
    }
    if (deferrable || (deferrableOnce && !onceCalled)) {
      this.data.set(event, props);
    }
  };

  sub = <K extends keyof T>(
    event: K | K[],
    listener: (_event: K, props: T[K]) => void,
    deferrable = false,
    deferrableOnce = false,
  ): (() => void) => {
    if (!Array.isArray(event)) {
      event = [event];
    }
    event.forEach((e) => {
      // console.log('sub event', event, { deferrable, deferrableOnce });
      let listeners = this.map.get(e);
      if (!listeners) {
        listeners = [];
        this.map.set(e, listeners);
      }
      // @ts-ignore todo
      listeners.push(listener);

      if (deferrable || deferrableOnce) {
        const val = this.data.get(e);
        if (val) {
          // defer to avoid calling the listener immediately
          setTimeout(() => {
            listener(e, val as T[K]);
          }, 1);
          // leave the event in the data map so it can be used for future subscribers
          if (deferrableOnce) {
            this.data.delete(e);
          }
        }
      }
    });

    return () => {
      (event as K[]).forEach((e) => {
        let _listeners = this.map.get(e);
        if (_listeners) {
          _listeners = _listeners.filter((l) => l !== listener);
          this.map.set(e, _listeners);
        }
      });
    };
  };
}
